import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Reveal from "@/components/Reveal";
import { MapPin, Phone, Mail, Clock, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const eventTypes = ["Matrimonio", "Diciottesimo", "Cerimonia", "Evento Aziendale", "Compleanno", "Altro"];

const initialForm = {
  nome: "",
  email: "",
  telefono: "",
  tipo_evento: "",
  data_evento: "",
  ospiti: "",
  messaggio: "",
};

export default function Contatti() {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/quote-requests`, {
        ...form,
        ospiti: form.ospiti ? Number(form.ospiti) : null,
        data_evento: form.data_evento || null,
        messaggio: form.messaggio || null,
      });
      setSent(true);
      setForm(initialForm);
      toast.success("Richiesta inviata", {
        description: "Ti ricontatteremo entro 24 ore con una proposta su misura.",
      });
    } catch (err) {
      toast.error("Qualcosa è andato storto", {
        description: "Riprova tra poco o scrivici direttamente via email.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contatti" data-testid="contatti-section" className="bg-deepblue px-6 py-28 md:py-40 lg:px-10">
      <div className="mx-auto grid max-w-7xl gap-16 lg:grid-cols-5 lg:gap-24">
        <div className="lg:col-span-2">
          <Reveal>
            <p className="mb-6 flex items-center gap-4 text-xs font-medium uppercase tracking-[0.35em] text-dusty">
              <span className="h-px w-10 bg-dusty/70" /> Contatti
            </p>
            <h2 className="font-serif text-4xl leading-tight tracking-tight text-linen sm:text-5xl">
              Raccontaci il tuo <span className="italic text-dusty">evento</span>.
            </h2>
            <p className="mt-6 max-w-md text-base font-light leading-relaxed text-linen/60">
              Compila il form e riceverai una proposta personalizzata. Ogni evento
              è unico: anche il nostro preventivo lo è.
            </p>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="mt-12 flex flex-col gap-6 text-sm text-linen/70" data-testid="contact-info">
              <p className="flex items-center gap-4">
                <MapPin className="h-4 w-4 shrink-0 text-sand" /> Via Guido Figliolini 33, 00173 Roma
              </p>
              <p className="flex items-center gap-4">
                <Phone className="h-4 w-4 shrink-0 text-sand" /> +39 351 313 8542 · Referente Enrico
              </p>
              <p className="flex items-center gap-4">
                <Mail className="h-4 w-4 shrink-0 text-sand" /> clubstareventituscolana@gmail.com
              </p>
              <p className="flex items-center gap-4">
                <Clock className="h-4 w-4 shrink-0 text-sand" /> Lun – Dom, 9:00 – 20:00 · Su appuntamento
              </p>
              <div className="mt-2 flex items-center gap-4">
                <a
                  href="https://www.instagram.com/club_stareventituscolana/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Apri Instagram"
                  data-testid="instagram-contact-link"
                  className="flex h-11 w-11 items-center justify-center rounded-full bg-[#E1306C] transition-transform duration-300 hover:scale-110"
                >
                  <svg viewBox="0 0 448 512" className="h-5 w-5 fill-white" aria-hidden="true">
                    <path d="M224.1 141c-63.6 0-114.9 51.3-114.9 114.9s51.3 114.9 114.9 114.9S339 319.5 339 255.9 287.7 141 224.1 141zm0 189.6c-41.1 0-74.7-33.5-74.7-74.7s33.5-74.7 74.7-74.7 74.7 33.5 74.7 74.7-33.6 74.7-74.7 74.7zm146.4-194.3c0 14.9-12 26.8-26.8 26.8-14.9 0-26.8-12-26.8-26.8s12-26.8 26.8-26.8 26.8 12 26.8 26.8zm76.1 27.2c-1.7-35.9-9.9-67.7-36.2-93.9-26.2-26.2-58-34.4-93.9-36.2-37-2.1-147.9-2.1-184.9 0-35.8 1.7-67.6 9.9-93.9 36.1s-34.4 58-36.2 93.9c-2.1 37-2.1 147.8 0 184.9 1.7 35.9 9.9 67.7 36.2 93.9s58 34.4 93.9 36.2c37 2.1 147.9 2.1 184.9 0 35.9-1.7 67.7-9.9 93.9-36.2 26.2-26.2 34.4-58 36.2-93.9 2.1-37 2.1-147.8 0-184.8zM398.8 388c-7.8 19.6-22.9 34.7-42.6 42.6-29.5 11.7-99.5 9-132.1 9s-102.7 2.6-132.1-9c-19.6-7.8-34.7-22.9-42.6-42.6-11.7-29.5-9-99.5-9-132.1s-2.6-102.7 9-132.1c7.8-19.6 22.9-34.7 42.6-42.6 29.5-11.7 99.5-9 132.1-9s102.7-2.6 132.1 9c19.6 7.8 34.7 22.9 42.6 42.6 11.7 29.5 9 99.5 9 132.1s2.7 102.7-9 132.1z" />
                  </svg>
                </a>
                <a
                  href="https://www.facebook.com/ClubStarEventiTuscolana/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Apri Facebook"
                  data-testid="facebook-contact-link"
                  className="flex h-11 w-11 items-center justify-center rounded-full bg-[#1877F2] transition-transform duration-300 hover:scale-110"
                >
                  <svg viewBox="0 0 320 512" className="h-5 w-5 fill-white" aria-hidden="true">
                    <path d="M279.14 288l14.22-92.66h-88.91v-60.13c0-25.35 12.42-50.06 52.24-50.06h40.42V6.26S260.43 0 225.36 0c-73.22 0-121.08 44.38-121.08 124.72v70.62H22.89V288h81.39v224h100.17V288z" />
                  </svg>
                </a>
                <span className="text-xs uppercase tracking-[0.2em] text-linen/40">Seguici sui social</span>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.25}>
            <div className="mt-10 overflow-hidden border border-white/10" data-testid="google-map-embed">
              <iframe
                title="Mappa: Club Star Eventi, Via Guido Figliolini 33, Roma"
                src="https://www.google.com/maps?q=Via+Guido+Figliolini+33,+00173+Roma&output=embed"
                className="h-72 w-full"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1} className="lg:col-span-3">
          <form onSubmit={submit} data-testid="quote-request-form" className="grid grid-cols-1 gap-8 sm:grid-cols-2">
            <div>
              <label htmlFor="nome" className="mb-2 block text-xs uppercase tracking-[0.2em] text-linen/50">
                Nome e Cognome *
              </label>
              <input id="nome" data-testid="input-nome" required value={form.nome} onChange={set("nome")} className="lux-input" placeholder="Maria Rossi" />
            </div>
            <div>
              <label htmlFor="telefono" className="mb-2 block text-xs uppercase tracking-[0.2em] text-linen/50">
                Telefono *
              </label>
              <input id="telefono" data-testid="input-telefono" required type="tel" value={form.telefono} onChange={set("telefono")} className="lux-input" placeholder="+39 333 000 0000" />
            </div>
            <div>
              <label htmlFor="email" className="mb-2 block text-xs uppercase tracking-[0.2em] text-linen/50">
                Email *
              </label>
              <input id="email" data-testid="input-email" required type="email" value={form.email} onChange={set("email")} className="lux-input" placeholder="maria@email.it" />
            </div>
            <div>
              <label htmlFor="tipo_evento" className="mb-2 block text-xs uppercase tracking-[0.2em] text-linen/50">
                Tipo di evento *
              </label>
              <select
                id="tipo_evento"
                data-testid="select-tipo-evento"
                required
                value={form.tipo_evento}
                onChange={set("tipo_evento")}
                className="lux-input cursor-pointer appearance-none"
              >
                <option value="" disabled className="bg-deepblue">
                  Seleziona…
                </option>
                {eventTypes.map((t) => (
                  <option key={t} value={t} className="bg-deepblue">
                    {t}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="data_evento" className="mb-2 block text-xs uppercase tracking-[0.2em] text-linen/50">
                Data dell'evento
              </label>
              <input
                id="data_evento"
                data-testid="input-data-evento"
                type="date"
                value={form.data_evento}
                onChange={set("data_evento")}
                className="lux-input [color-scheme:dark]"
              />
            </div>
            <div>
              <label htmlFor="ospiti" className="mb-2 block text-xs uppercase tracking-[0.2em] text-linen/50">
                Numero di ospiti
              </label>
              <input
                id="ospiti"
                data-testid="input-ospiti"
                type="number"
                min="1"
                max="2000"
                value={form.ospiti}
                onChange={set("ospiti")}
                className="lux-input"
                placeholder="100"
              />
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="messaggio" className="mb-2 block text-xs uppercase tracking-[0.2em] text-linen/50">
                Messaggio
              </label>
              <textarea
                id="messaggio"
                data-testid="textarea-messaggio"
                rows={3}
                value={form.messaggio}
                onChange={set("messaggio")}
                className="lux-input resize-none"
                placeholder="Raccontaci come immagini il tuo evento…"
              />
            </div>

            <div className="sm:col-span-2">
              {sent && (
                <p data-testid="quote-success-message" className="mb-6 flex items-center gap-2 text-sm text-sand">
                  <CheckCircle2 className="h-4 w-4" /> Richiesta inviata con successo. A prestissimo!
                </p>
              )}
              <button
                type="submit"
                data-testid="submit-preventivo-button"
                disabled={loading}
                className="rounded-full bg-sand px-10 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-white transition-all duration-300 hover:tracking-[0.3em] hover:bg-dusty disabled:cursor-not-allowed disabled:opacity-50"
              >
                {loading ? "Invio in corso…" : "Invia richiesta"}
              </button>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
